/**
 * SearchStateManager
 *
 * Manages the state of the instant search modal, including products and collections counts,
 * loading states, and search term. Provides computed properties for common state queries.
 */

interface SearchResourceState {
  count: number
  loading: boolean
}

export class SearchStateManager {
  private state = {
    products: {
      count: 0,
      loading: false
    } as SearchResourceState,
    collections: {
      count: 0,
      loading: false
    } as SearchResourceState,
    searchTerm: ''
  }

  /**
   * Updates the products state
   */
  updateProducts(count: number, loading: boolean): void {
    this.state.products.count = count
    this.state.products.loading = loading
  }

  /**
   * Updates the collections state
   */
  updateCollections(count: number, loading: boolean): void {
    this.state.collections.count = count
    this.state.collections.loading = loading
  }

  /**
   * Updates the search term
   */
  updateSearchTerm(searchTerm: string): void {
    this.state.searchTerm = searchTerm
  }

  /**
   * Resets all state to initial values
   */
  reset(): void {
    this.state.products.count = 0
    this.state.products.loading = false
    this.state.collections.count = 0
    this.state.collections.loading = false
    this.state.searchTerm = ''
  }

  // Getters for direct state access
  get productsCount(): number {
    return this.state.products.count
  }

  get productsLoading(): boolean {
    return this.state.products.loading
  }

  get collectionsCount(): number {
    return this.state.collections.count
  }

  get collectionsLoading(): boolean {
    return this.state.collections.loading
  }

  get searchTerm(): string {
    return this.state.searchTerm
  }

  /**
   * Returns true if either products or collections are currently loading
   */
  get isAnyLoading(): boolean {
    return this.state.products.loading || this.state.collections.loading
  }

  /**
   * Returns true if both products and collections have zero results
   */
  get hasNoResults(): boolean {
    return this.state.products.count === 0 && this.state.collections.count === 0
  }

  /**
   * Returns true if there are any product results
   */
  get hasProducts(): boolean {
    return this.state.products.count > 0
  }

  /**
   * Returns true if there are any collection results
   */
  get hasCollections(): boolean {
    return this.state.collections.count > 0
  }

  /**
   * Returns true if there are any results (products or collections)
   */
  get hasAnyResults(): boolean {
    return this.hasProducts || this.hasCollections
  }
}
